import React, { useState, useEffect } from 'react';
import { questionnaireService } from '../services/questionnaireService';
import { Question } from '../types';
import { useToast } from '../context/ToastContext';

interface PaginationConfigTabProps {
  questionnaireId: number;
  questionnaire: any;
  questions: Question[];
  onUpdate: () => void;
}

const PaginationConfigTab: React.FC<PaginationConfigTabProps> = ({
  questionnaireId,
  questionnaire,
  questions,
  onUpdate
}) => {
  const { showToast } = useToast();
  const [paginationEnabled, setPaginationEnabled] = useState(false);
  const [showQuestionNumber, setShowQuestionNumber] = useState(true);
  const [showResultToUser, setShowResultToUser] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (questionnaire) {
      setPaginationEnabled(questionnaire.paginationEnabled || false);
      setShowQuestionNumber(questionnaire.showQuestionNumber !== false);
      // 旧问卷 showResultToUser 可能为 undefined，按默认开启处理
      setShowResultToUser(questionnaire.showResultToUser !== false);
    }
  }, [questionnaire]);

  useEffect(() => {
    if (questionnaire) {
      const changed = paginationEnabled !== (questionnaire.paginationEnabled || false)
        || showQuestionNumber !== (questionnaire.showQuestionNumber !== false)
        || showResultToUser !== (questionnaire.showResultToUser !== false);
      setHasChanges(changed);
    }
  }, [paginationEnabled, showQuestionNumber, showResultToUser, questionnaire]);

  const handleSave = async () => {
    try {
      setIsSaving(true);

      await questionnaireService.updateQuestionnaire(questionnaireId, {
        title: questionnaire.title,
        description: questionnaire.description,
        paginationEnabled,
        showQuestionNumber,
        showResultToUser,
      });

      showToast('显示设置已保存', 'success');
      setHasChanges(false);
      onUpdate();
    } catch (error) {
      console.error('Failed to save display config:', error);
      showToast('保存失败，请重试', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (questionnaire) {
      setPaginationEnabled(questionnaire.paginationEnabled || false);
      setShowQuestionNumber(questionnaire.showQuestionNumber !== false);
      setShowResultToUser(questionnaire.showResultToUser !== false);
      setHasChanges(false);
    }
  };

  // Build page structure from questions' pageBreakBefore flags
  const pages: Question[][] = [];
  let currentPage: Question[] = [];
  const sortedQuestions = [...questions].sort((a, b) => a.orderNum - b.orderNum);

  sortedQuestions.forEach((q, i) => {
    if (i > 0 && q.pageBreakBefore) {
      pages.push(currentPage);
      currentPage = [];
    }
    currentPage.push(q);
  });
  if (currentPage.length > 0) pages.push(currentPage);

  const pageBreakCount = questions.filter(q => q.pageBreakBefore).length;

  return (
    <div className="space-y-6">
      {/* 说明卡片 */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">关于显示设置</h3>
            <div className="mt-2 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li><strong>自动题号：</strong>控制用户端是否显示自动递增的题号（1、2、3…）。各题可额外设置自定义题号（如 1.1、一、A），自定义题号始终显示</li>
                <li><strong>分页：</strong>将问卷题目分页显示，在"问题列表"标签页中插入分页符来设置分页位置</li>
                <li><strong>结果反馈：</strong>关闭后，用户提交问卷后将看不到"查看报告"按钮和个人报告页（仅作数据收集用途）</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 配置选项 */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">基本设置</h3>

        <div className="space-y-6">
          {/* 显示自动题号 */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="showQuestionNumber"
                checked={showQuestionNumber}
                onChange={(e) => setShowQuestionNumber(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="showQuestionNumber" className="font-medium text-gray-700 cursor-pointer">
                显示自动题号
              </label>
              <p className="text-sm text-gray-500">启用后，用户端将显示自动递增的题号（1、2、3…）。关闭后仅显示各题单独设置的自定义题号</p>
            </div>
          </div>

          {/* 启用分页 */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="paginationEnabled"
                checked={paginationEnabled}
                onChange={(e) => setPaginationEnabled(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="paginationEnabled" className="font-medium text-gray-700 cursor-pointer">
                启用分页显示
              </label>
              <p className="text-sm text-gray-500">启用后，可在问题列表中插入分页符来控制每页显示的题目</p>
            </div>
          </div>

          {/* 用户端结果反馈可见性 */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="showResultToUser"
                checked={showResultToUser}
                onChange={(e) => setShowResultToUser(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="showResultToUser" className="font-medium text-gray-700 cursor-pointer">
                向用户端展示问卷结果反馈
              </label>
              <p className="text-sm text-gray-500">
                启用后，用户提交问卷后可点击"查看报告"查看个人指标得分和结论。
                关闭后该按钮和报告页都不显示，问卷仅作数据收集用途
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 分页结构预览 */}
      {paginationEnabled && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">分页结构预览</h3>
          {pages.length === 0 ? (
            <p className="text-sm text-gray-500">暂无题目</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-6 text-sm mb-4">
                <div>
                  <span className="text-gray-500">总题目数：</span>
                  <span className="font-semibold text-gray-900">{questions.length} 题</span>
                </div>
                <div>
                  <span className="text-gray-500">分页符数：</span>
                  <span className="font-semibold text-gray-900">{pageBreakCount} 个</span>
                </div>
                <div>
                  <span className="text-gray-500">总页数：</span>
                  <span className="font-semibold text-primary-600">{pages.length} 页</span>
                </div>
              </div>

              {pages.map((page, pageIdx) => {
                // Calculate global start index for this page
                let globalStart = 0;
                for (let i = 0; i < pageIdx; i++) globalStart += pages[i].length;
                return (
                <div key={pageIdx} className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded text-xs font-medium">
                      第 {pageIdx + 1} 页
                    </span>
                    <span className="text-xs text-gray-500">{page.length} 题</span>
                  </div>
                  <div className="space-y-1">
                    {page.map((q, qIdx) => (
                      <div key={q.id} className="text-xs text-gray-600 bg-white px-2 py-1.5 rounded border border-gray-200 flex items-center gap-1.5">
                        <span className="text-gray-400 font-medium shrink-0">Q{globalStart + qIdx + 1}</span>
                        <span className="truncate">{q.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* 保存按钮 */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="text-sm text-gray-600">
          {hasChanges && (
            <span className="text-yellow-600 font-medium">● 有未保存的更改</span>
          )}
        </div>
        <div className="flex gap-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? '保存中...' : '保存配置'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaginationConfigTab;
